import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Provider } from "@ethersproject/abstract-provider";
// import { Signer } from "@ethersproject/abstract-signer";
import { getNetwork } from "@ethersproject/networks";
import { Web3Provider } from "@ethersproject/providers";
import { useWeb3React } from "@web3-react/core";

import { isBatchedProvider, isWebSocketAugmentedProvider } from "@liquity/providers";
import {
  BlockPolledLiquityStore,
  EthersLiquity,
  EthersLiquityWithStore,
  _connectByChainId
} from "@liquity/lib-ethers";

import { LiquityFrontendConfig, getConfig } from "../config";
import { chain } from "lodash"

type LiquityContextValue = {
  config: LiquityFrontendConfig;
  account: string;
  provider: Provider;
  liquity: EthersLiquityWithStore<BlockPolledLiquityStore>;
};

const LiquityContext = createContext<LiquityContextValue | undefined>(undefined);

type LiquityProviderProps = {
  loader?: React.ReactNode;
  unsupportedNetworkFallback?: (chainId: number, supportedNetworkNames?: string[]) => React.ReactNode;
  unsupportedMainnetFallback?: React.ReactNode;
};

// const wsParams = (network: string, infuraApiKey: string): [string, string] => [
//   `wss://${network === "homestead" ? "mainnet" : network}.infura.io/ws/v3/${infuraApiKey}`,
//   network
// ];
const wsParams = (network: string, alchemyApiKey: string): [string, string] => [
  `wss://arb-mainnet.g.alchemy.com/v2/${alchemyApiKey}`,
  network
];

const webSocketSupportedNetworks = ["sepolia", "arbitrum"];

export const LiquityProvider: React.FC<LiquityProviderProps> = ({
  children,
  loader,
  unsupportedNetworkFallback,
  unsupportedMainnetFallback
}) => {
  const { library: provider, account, chainId } = useWeb3React<Web3Provider>();
  const [config, setConfig] = useState<LiquityFrontendConfig>();

  const connection = useMemo(() => {
    if (config && provider && account && chainId) {
      try {
        return _connectByChainId(provider, provider.getSigner(account), chainId, {
          userAddress: account,
          frontendTag: config.frontendTag,
          useStore: "blockPolled"
        });
      } catch {}
    }
  }, [config, provider, account, chainId]);

  useEffect(() => {
    getConfig().then(setConfig);
  }, []);

  useEffect(() => {
    if (config && connection) {
      const { provider, chainId } = connection;

      if (isBatchedProvider(provider) && provider.chainId !== chainId) {
        provider.chainId = chainId;
      }

      // if (isWebSocketAugmentedProvider(provider)) {
      //   const network = getNetwork(chainId);
      //   if (
      //     network.name &&
      //     webSocketSupportedNetworks.includes(network.name) &&
      //     // config.infuraApiKey
      //     config.alchemyApiKey
      //   ) {
      //     provider.openWebSocket(...wsParams(network.name, config.alchemyApiKey));
      //   } else if (connection._isDev) {
      //     provider.openWebSocket(`ws://${window.location.hostname}:8546`, chainId);
      //   }

      //   return () => {
      //     provider.closeWebSocket();
      //   };
      // }
    }
  }, [config, connection]);

  if (!config || !provider || !account || !chainId) {
    return <>{loader}</>;
  }

  if (config.testnetOnly && chainId === 1) {
    return <>{unsupportedMainnetFallback}</>;
  }

  const network = getNetwork(chainId)
  if (!config.allowedNetworks.includes(network.name)) {
    return unsupportedNetworkFallback ? <>{unsupportedNetworkFallback(chainId, config.allowedNetworks)}</> : null;
  }

  if (!connection) {
    return unsupportedNetworkFallback ? <>{unsupportedNetworkFallback(chainId, config.allowedNetworks)}</> : null;
  }

  const liquity = EthersLiquity._from(connection);
  liquity.store.logging = true;

  return (
    <LiquityContext.Provider value={{ config, account, provider, liquity }}>
      {children}
    </LiquityContext.Provider>
  );
};

export const useLiquity = () => {
  const liquityContext = useContext(LiquityContext);

  if (!liquityContext) {
    throw new Error("You must provide a LiquityContext via LiquityProvider");
  }

  return liquityContext;
};
